/**
 * The Readlang experiments framework is inspired by Duolingo's system, except that the experiment
 * condition is determined by hashing the userID + experiment ID, allowing the client to determine
 * the condition without needing to make a request to the server.
 */

/**
 * @typedef {"readerReview2"} ExperimentName
 */

/**
 * @typedef {{
 *   name: ExperimentName;
 *   conditions: string[];
 *   override?: string;
 * }} Experiment
 */

/**
 * @type {Experiment[]}
 */
const experiments = [
  {
    // Start date: 2024-06-18
    // Reader review defaults to ON for free users
    name: "readerReview2",
    conditions: ["control", "experiment"],
  },
];

const experimentMap = experiments.reduce(
  (map, experiment) => ({
    [experiment.name]: experiment,
    ...map,
  }),
  {}
);

export const activeExperiments = experiments.map(
  (experiment) => experiment.name
);

/**
 * Hashes the input string, outputting a 32bit integer number.
 *
 * Adapted from https://stackoverflow.com/a/7616484
 *
 * @param {string} input
 *
 * @returns {number}
 */
const hash = function (input) {
  let hashValue = 0,
    i,
    chr;
  if (input.length === 0) return hashValue;
  for (i = 0; i < input.length; i++) {
    chr = input.charCodeAt(i);
    hashValue = (hashValue << 5) - hashValue + chr;
    hashValue |= 0; // Convert to 32bit integer
  }
  return hashValue;
};

/**
 * @param {any} userID
 * @param {ExperimentName} experimentName
 *
 * @returns {{condition: string, override: boolean}} condition and override
 */
export const getCondition = (userID, experimentName) => {
  const experiment = experimentMap[experimentName];

  if (!experiment) {
    throw Error(`Invalid experiment name: ${experimentName}`);
  }

  if (experiment.override) {
    return {
      condition: experiment.override,
      override: true,
    };
  }

  const conditionIndex = Math.abs(
    hash([userID, experimentName].join("\n")) % experiment.conditions.length
  );

  return {
    condition: experiment.conditions[conditionIndex],
    override: false,
  };
};
